import { Injectable, inject, WritableSignal } from '@angular/core';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';

import { Invoice, Article } from '@shared-models';

import { NoteArticleType } from '../enums/note-article-type.enum';
import { NoteItemForm } from '../models/note-item-form.model';

@Injectable()
export class NoteFormManagerService {
  private readonly fb = inject(NonNullableFormBuilder);

  createEmptyItemGroup(invoice: Invoice | undefined): FormGroup<NoteItemForm> {
    const group = this.fb.group<NoteItemForm>({
      warehouse: this.fb.control(
        invoice?.venta?.[0]?.almacen?.[0]?.nombre ?? '',
      ),
      article: this.fb.control<Article | null>(null, [Validators.required]),
      quantity: this.fb.control(1, [Validators.min(1), Validators.max(1)]),
      price: this.fb.control(0, [Validators.min(0)]),
      subtotal: this.fb.control(0),
      totalWithTax: this.fb.control(0),
    });

    group.controls.quantity.disable({ emitEvent: false });
    return group;
  }

  createItemGroupFromSale(sale: Invoice['venta'][0]): FormGroup<NoteItemForm> {
    const qtyMax = sale.cantidad ?? 0;

    return this.fb.group<NoteItemForm>({
      warehouse: this.fb.control(sale.almacen?.[0]?.nombre ?? ''),
      article: this.fb.control<Article | null>(sale.articulo?.[0] ?? null),
      quantity: this.fb.control(0, [Validators.min(0), Validators.max(qtyMax)]),
      price: this.fb.control(0, [Validators.min(0)]),
      subtotal: this.fb.control(0),
      totalWithTax: this.fb.control(0),
    });
  }

  disableAndSetFullCancellation(
    groups: FormGroup<NoteItemForm>[],
    articleType: NoteArticleType,
    invoice: Invoice | undefined,
    sales: Invoice['venta'],
  ): void {
    if (articleType === NoteArticleType.BY_TOTAL) {
      const group = groups[0];
      if (group) {
        const invoiceTotal = invoice?.venta?.[0]?.total ?? 0;

        group.controls.price.setValue(invoiceTotal, { emitEvent: true });
        group.controls.quantity.setValue(1, { emitEvent: true });

        group.controls.price.disable({ emitEvent: false });
        group.controls.quantity.disable({ emitEvent: false });
      }
    } else {
      groups.forEach((group, index) => {
        const originalSale = sales[index];
        if (!originalSale) return;

        group.controls.quantity.setValue(originalSale.cantidad, {
          emitEvent: true,
        });
        group.controls.price.setValue(originalSale.precio, { emitEvent: true });

        group.controls.quantity.disable({ emitEvent: false });
        group.controls.price.disable({ emitEvent: false });
      });
    }
  }

  enableAllControls(groups: FormGroup<NoteItemForm>[]): void {
    groups.forEach((group) => {
      if (group.controls.quantity.disabled) {
        group.controls.quantity.enable({ emitEvent: false });
      }
      if (group.controls.price.disabled) {
        group.controls.price.enable({ emitEvent: false });
      }
    });
  }

  lockPriceByQuantity(
    group: FormGroup<NoteItemForm>,
    index: number,
    originalPrice: number,
    mode: 'absolute' | 'percentage',
    priceLockedByQuantity: WritableSignal<Set<number>>,
    quantityLockedByPrice: WritableSignal<Set<number>>,
  ): void {
    const newPrice = mode === 'percentage' ? 0 : originalPrice;

    const currentPrice = group.controls.price.value;
    if (currentPrice !== newPrice) {
      group.controls.price.setValue(newPrice, { emitEvent: false });
    }

    if (!group.controls.price.disabled) {
      group.controls.price.disable({ emitEvent: false });
    }

    priceLockedByQuantity.update((set) => new Set(set).add(index));
    quantityLockedByPrice.update((set) => {
      const newSet = new Set(set);
      newSet.delete(index);
      return newSet;
    });
  }

  unlockPrice(
    group: FormGroup<NoteItemForm>,
    index: number,
    priceLockedByQuantity: WritableSignal<Set<number>>,
  ): void {
    if (group.controls.price.disabled) {
      group.controls.price.enable({ emitEvent: false });
    }

    priceLockedByQuantity.update((set) => {
      const newSet = new Set(set);
      newSet.delete(index);
      return newSet;
    });
  }

  lockQuantityByPrice(
    group: FormGroup<NoteItemForm>,
    index: number,
    originalQty: number,
    quantityLockedByPrice: WritableSignal<Set<number>>,
  ): void {
    const currentQty = Number(group.controls.quantity.value) || 0;

    if (currentQty !== originalQty) {
      group.controls.quantity.setValue(originalQty, { emitEvent: false });
    }

    if (!group.controls.quantity.disabled) {
      group.controls.quantity.disable({ emitEvent: false });
    }

    quantityLockedByPrice.update((set) => new Set(set).add(index));
  }

  unlockQuantityAndReset(
    group: FormGroup<NoteItemForm>,
    index: number,
    quantityLockedByPrice: WritableSignal<Set<number>>,
  ): void {
    if (group.controls.quantity.disabled) {
      group.controls.quantity.enable({ emitEvent: false });
    }

    const currentQty = Number(group.controls.quantity.value) || 0;
    if (currentQty !== 0) {
      group.controls.quantity.setValue(0, { emitEvent: false });
    }

    quantityLockedByPrice.update((set) => {
      const newSet = new Set(set);
      newSet.delete(index);
      return newSet;
    });
  }
}
